import { useEffect, useCallback } from 'react';
import { PaperAuthor } from '@/types/paper';
import { UploadMethod, UploadSource, VisibilityChoice, PaperVisibility } from '../contexts/types';

const STORAGE_KEY = 'paperverse_upload_form';

interface PersistedFormData {
  visibilityChoice: VisibilityChoice;
  sourceChoice: UploadSource;
  uploadMethod: UploadMethod;
  visibility: PaperVisibility;
  externalUrl: string;
  arxivUrl: string;
  title: string;
  abstract: string;
  category: string;
  keywords: string[];
  additionalAuthors: PaperAuthor[];
  requestReview: boolean;
  selectedReviewers: string[];
  // Note: File cannot be persisted to sessionStorage - user will need to re-upload
}

interface FormDataSetters {
  setVisibilityChoice: (v: VisibilityChoice) => void;
  setSourceChoice: (s: UploadSource) => void;
  setUploadMethod: (m: UploadMethod) => void;
  setVisibility: (v: PaperVisibility) => void;
  setExternalUrl: (u: string) => void;
  setArxivUrl: (u: string) => void;
  setTitle: (t: string) => void;
  setAbstract: (a: string) => void;
  setCategory: (c: string) => void;
  setKeywords: (k: string[]) => void;
  setAdditionalAuthors: (a: PaperAuthor[]) => void;
  setRequestReview: (r: boolean) => void;
  setSelectedReviewers: (s: string[]) => void;
}

interface UseFormPersistenceProps {
  formData: {
    visibilityChoice: VisibilityChoice;
    sourceChoice: UploadSource;
    uploadMethod: UploadMethod;
    visibility: PaperVisibility;
    externalUrl: string;
    arxivUrl: string;
    title: string;
    abstract: string;
    category: string;
    keywords: string[];
    additionalAuthors: PaperAuthor[];
    requestReview: boolean;
    selectedReviewers: string[];
  };
  setters: FormDataSetters;
}

export const useFormPersistence = ({ formData, setters }: UseFormPersistenceProps) => {
  // Save form data to sessionStorage whenever it changes
  const saveFormData = useCallback(() => {
    try {
      const dataToSave: PersistedFormData = {
        visibilityChoice: formData.visibilityChoice,
        sourceChoice: formData.sourceChoice,
        uploadMethod: formData.uploadMethod,
        visibility: formData.visibility,
        externalUrl: formData.externalUrl,
        arxivUrl: formData.arxivUrl,
        title: formData.title,
        abstract: formData.abstract,
        category: formData.category,
        keywords: formData.keywords,
        additionalAuthors: formData.additionalAuthors,
        requestReview: formData.requestReview,
        selectedReviewers: formData.selectedReviewers,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [formData]);

  // Restore form data from sessionStorage
  const restoreFormData = useCallback(() => {
    try {
      const savedData = sessionStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsed: PersistedFormData = JSON.parse(savedData);
        
        if (parsed.visibilityChoice !== null) setters.setVisibilityChoice(parsed.visibilityChoice);
        if (parsed.sourceChoice !== null) setters.setSourceChoice(parsed.sourceChoice);
        if (parsed.uploadMethod) setters.setUploadMethod(parsed.uploadMethod);
        if (parsed.visibility) setters.setVisibility(parsed.visibility);
        if (parsed.externalUrl) setters.setExternalUrl(parsed.externalUrl);
        if (parsed.arxivUrl) setters.setArxivUrl(parsed.arxivUrl);
        if (parsed.title) setters.setTitle(parsed.title);
        if (parsed.abstract) setters.setAbstract(parsed.abstract);
        if (parsed.category) setters.setCategory(parsed.category);
        if (parsed.keywords?.length) setters.setKeywords(parsed.keywords);
        if (parsed.additionalAuthors?.length) setters.setAdditionalAuthors(parsed.additionalAuthors);
        if (typeof parsed.requestReview === 'boolean') setters.setRequestReview(parsed.requestReview);
        if (parsed.selectedReviewers?.length) setters.setSelectedReviewers(parsed.selectedReviewers);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error restoring form data:', error);
      return false;
    }
  }, [setters]);

  // Clear persisted form data
  const clearFormData = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing form data:', error);
    }
  }, []);

  // Check if there's pending form data
  const hasPendingFormData = useCallback(() => {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  }, []);

  // Auto-save on form data changes (debounced via effect dependencies)
  useEffect(() => {
    // Only save if user has entered actual content (not just navigation choices)
    const hasProgress = formData.title.trim() || 
                       formData.abstract.trim() || 
                       formData.externalUrl.trim() ||
                       formData.arxivUrl.trim();
    if (hasProgress) {
      saveFormData();
    }
  }, [formData, saveFormData]);

  return {
    saveFormData,
    restoreFormData,
    clearFormData,
    hasPendingFormData,
  };
};
